import React, { useState, useEffect } from 'react';
import { userAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { useNotification } from '../../context/NotificationContext';
import { 
  X, Shield, Key, Smartphone, Monitor, Globe, Mail,
  Plus, Trash2, Check, AlertTriangle, Eye, EyeOff,
  RefreshCw, Copy, CheckCircle, ChevronRight
} from 'lucide-react';

// ==================== CHANGE PASSWORD MODAL ====================
export const ChangePasswordModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const { toast } = useNotification();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (newPassword !== confirmPassword) {
      setError(t('passwordsNotMatch'));
      return;
    }
    if (newPassword.length < 6) {
      setError(t('passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      await userAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Успешно', 'Пароль успешно изменён!');
      onClose();
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка смены пароля');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1e2329',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '400px',
          padding: '24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ color: '#eaecef', fontSize: '18px', fontWeight: '600', margin: 0 }}>Смена пароля</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={24} color="#848e9c" />
          </button>
        </div>

        {error && (
          <div style={{ backgroundColor: '#f6465d20', border: '1px solid #f6465d', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#f6465d', fontSize: '13px' }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: '#848e9c', fontSize: '13px', marginBottom: '8px' }}>Текущий пароль</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: '44px',
                backgroundColor: '#0b0e11',
                border: '1px solid #2b3139',
                borderRadius: '8px',
                color: '#eaecef',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <button onClick={() => setShowCurrent(!showCurrent)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
              {showCurrent ? <EyeOff size={18} color="#848e9c" /> : <Eye size={18} color="#848e9c" />}
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: '#848e9c', fontSize: '13px', marginBottom: '8px' }}>Новый пароль</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: '44px',
                backgroundColor: '#0b0e11',
                border: '1px solid #2b3139',
                borderRadius: '8px',
                color: '#eaecef',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <button onClick={() => setShowNew(!showNew)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
              {showNew ? <EyeOff size={18} color="#848e9c" /> : <Eye size={18} color="#848e9c" />}
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', color: '#848e9c', fontSize: '13px', marginBottom: '8px' }}>Подтвердите пароль</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#0b0e11',
              border: '1px solid #2b3139',
              borderRadius: '8px',
              color: '#eaecef',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading || !currentPassword || !newPassword || !confirmPassword}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: '#f0b90b',
            color: '#000',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Сохранение...' : 'Изменить пароль'}
        </button>
      </div>
    </div>
  );
};

// ==================== 2FA SETUP MODAL ====================
export const TwoFactorModal = ({ isOpen, onClose }) => {
  const { toast } = useNotification();
  const [step, setStep] = useState(1);
  const [secret, setSecret] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState('none');
  const [copied, setCopied] = useState(false);
  const [hasAuthenticatorSetup, setHasAuthenticatorSetup] = useState(false);

  useEffect(() => {
    if (isOpen) {
      check2FAStatus();
    }
  }, [isOpen]);

  const check2FAStatus = async () => {
    try {
      const res = await userAPI.get2FAStatus();
      setIs2FAEnabled(res.data.enabled);
      setTwoFactorMethod(res.data.method || 'none');
      setHasAuthenticatorSetup(res.data.enabled && res.data.method !== 'none');
      
      // Also check email 2FA status
      try {
        const emailRes = await userAPI.getEmail2FAStatus();
        setEmailVerified(emailRes.data.email_verified);
        setEmailMasked(emailRes.data.email_masked);
      } catch (e) {
        console.log('Email 2FA status not available');
      }
    } catch (err) {
      console.error('Failed to check 2FA status');
    }
  };

  // Email 2FA state
  const [emailStep, setEmailStep] = useState(1); // 1 = initial, 2 = enter code
  const [emailCode, setEmailCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailMasked, setEmailMasked] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const setupEmail2FA = async () => {
    setEmailLoading(true);
    try {
      const res = await userAPI.sendEmail2FACode({});
      setEmailMasked(res.data.email);
      setEmailStep(2);
      toast.info('Код отправлен', `Код отправлен на ${res.data.email}`);
    } catch (err) {
      toast.error('Ошибка', err.response?.data?.detail || 'Ошибка отправки кода');
    } finally {
      setEmailLoading(false);
    }
  };

  const verifyEmail2FA = async () => {
    if (emailCode.length !== 6) return;
    setEmailLoading(true);
    try {
      const res = await userAPI.verifyEmail2FA({ code: emailCode });
      setEmailVerified(true);
      setTwoFactorMethod(res.data.method);
      setIs2FAEnabled(true);
      setEmailStep(1);
      setEmailCode('');
      toast.success('Успешно', 'Email 2FA успешно включена!');
    } catch (err) {
      toast.error('Ошибка', err.response?.data?.detail || 'Неверный код');
    } finally {
      setEmailLoading(false);
    }
  };

  const resendEmailCode = async () => {
    setEmailLoading(true);
    try {
      await userAPI.resendEmail2FACode();
      toast.success('Отправлено', 'Код отправлен повторно');
    } catch (err) {
      toast.error('Ошибка', err.response?.data?.detail || 'Ошибка отправки кода');
    } finally {
      setEmailLoading(false);
    }
  };

  const setup2FA = async () => {
    setLoading(true);
    try {
      const res = await userAPI.setup2FA();
      setSecret(res.data.secret);
      setQrCode(res.data.qr_code);
      setStep(2);
    } catch (err) {
      toast.error('Ошибка', 'Ошибка настройки 2FA');
    } finally {
      setLoading(false);
    }
  };

  const verify2FA = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    try {
      await userAPI.verify2FA({ code });
      setIs2FAEnabled(true);
      setHasAuthenticatorSetup(true);
      setTwoFactorMethod('authenticator');
      setStep(1);
      toast.success('Успешно', '2FA успешно включена!');
    } catch (err) {
      toast.error('Ошибка', 'Неверный код');
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    if (!window.confirm('Отключить двухфакторную аутентификацию?')) return;
    setLoading(true);
    try {
      await userAPI.disable2FA({ code });
      setIs2FAEnabled(false);
      setTwoFactorMethod('none');
      setEmailVerified(false);
      setCode('');
      toast.info('2FA отключена', 'Двухфакторная аутентификация отключена');
    } catch (err) {
      toast.error('Ошибка', 'Неверный код');
    } finally {
      setLoading(false);
    }
  };

  const updateMethod = async (method) => {
    // For email, need to set it up first
    if ((method === 'email' || method === 'both') && !emailVerified) {
      setupEmail2FA();
      return;
    }
    
    setLoading(true);
    try {
      await userAPI.update2FASettings({ method });
      setTwoFactorMethod(method);
      setIs2FAEnabled(method !== 'none');
      const methodLabel = method === 'authenticator' ? 'Google Authenticator' :
        method === 'email' ? 'Email' :
        method === 'both' ? 'Оба метода' : 'Отключено';
      toast.success('Настройки обновлены', `Метод верификации: ${methodLabel}`);
    } catch (err) {
      toast.error('Ошибка', err.response?.data?.detail || 'Ошибка обновления настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1e2329',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '400px',
          padding: '24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ color: '#eaecef', fontSize: '18px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Smartphone size={20} color="#f0b90b" />
            Двухфакторная аутентификация
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={24} color="#848e9c" />
          </button>
        </div>

        {step === 1 && (
          <>
            <div style={{
              padding: '16px',
              backgroundColor: is2FAEnabled ? '#0ecb8115' : '#f0b90b15',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: is2FAEnabled ? '#0ecb81' : '#f0b90b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {is2FAEnabled ? <Check size={20} color="#000" /> : <Shield size={20} color="#000" />}
              </div>
              <div>
                <div style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>
                  {is2FAEnabled ? '2FA Включена' : '2FA Отключена'}
                </div>
                <div style={{ color: '#848e9c', fontSize: '12px' }}>
                  {is2FAEnabled ? 'Ваш аккаунт защищён' : 'Рекомендуем включить'}
                </div>
              </div>
            </div>

            {is2FAEnabled ? (
              <>
                {/* Method Selection */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', color: '#eaecef', fontSize: '14px', fontWeight: '500', marginBottom: '12px' }}>
                    Метод верификации при входе
                  </label>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <button
                      onClick={() => updateMethod('authenticator')}
                      disabled={loading}
                      style={{
                        padding: '12px 16px',
                        backgroundColor: twoFactorMethod === 'authenticator' ? '#f0b90b15' : '#0b0e11',
                        border: twoFactorMethod === 'authenticator' ? '1px solid #f0b90b' : '1px solid #2b3139',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        textAlign: 'left',
                      }}
                    >
                      <Smartphone size={20} color={twoFactorMethod === 'authenticator' ? '#f0b90b' : '#848e9c'} />
                      <div>
                        <div style={{ color: '#eaecef', fontSize: '14px', fontWeight: '500' }}>Google Authenticator</div>
                        <div style={{ color: '#848e9c', fontSize: '12px' }}>Код из приложения</div>
                      </div>
                      {twoFactorMethod === 'authenticator' && <Check size={18} color="#f0b90b" style={{ marginLeft: 'auto' }} />}
                    </button>

                    <button
                      onClick={() => updateMethod('email')}
                      disabled={loading || emailLoading}
                      style={{
                        padding: '12px 16px',
                        backgroundColor: twoFactorMethod === 'email' ? '#f0b90b15' : '#0b0e11',
                        border: twoFactorMethod === 'email' ? '1px solid #f0b90b' : '1px solid #2b3139',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        textAlign: 'left',
                      }}
                    >
                      <Mail size={20} color={twoFactorMethod === 'email' ? '#f0b90b' : '#848e9c'} />
                      <div>
                        <div style={{ color: '#eaecef', fontSize: '14px', fontWeight: '500' }}>Email код</div>
                        <div style={{ color: '#848e9c', fontSize: '12px' }}>
                          {emailVerified ? emailMasked : 'Настроить'}
                        </div>
                      </div>
                      {twoFactorMethod === 'email' && <Check size={18} color="#f0b90b" style={{ marginLeft: 'auto' }} />}
                    </button>

                    <button
                      onClick={() => updateMethod('both')}
                      disabled={loading || emailLoading || !hasAuthenticatorSetup}
                      style={{
                        padding: '12px 16px',
                        backgroundColor: twoFactorMethod === 'both' ? '#f0b90b15' : '#0b0e11',
                        border: twoFactorMethod === 'both' ? '1px solid #f0b90b' : '1px solid #2b3139',
                        borderRadius: '8px',
                        cursor: hasAuthenticatorSetup ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        textAlign: 'left',
                        opacity: hasAuthenticatorSetup ? 1 : 0.5,
                      }}
                    >
                      <Shield size={20} color={twoFactorMethod === 'both' ? '#f0b90b' : '#848e9c'} />
                      <div>
                        <div style={{ color: '#eaecef', fontSize: '14px', fontWeight: '500' }}>Оба метода</div>
                        <div style={{ color: '#848e9c', fontSize: '12px' }}>
                          {hasAuthenticatorSetup ? 'Authenticator + Email' : 'Сначала настройте Authenticator'}
                        </div>
                      </div>
                      {twoFactorMethod === 'both' && <Check size={18} color="#f0b90b" style={{ marginLeft: 'auto' }} />}
                    </button>
                  </div>
                </div>

                {/* Email 2FA Setup Step */}
                {emailStep === 2 && (
                  <div style={{ 
                    backgroundColor: '#0b0e11', 
                    borderRadius: '12px', 
                    padding: '16px',
                    marginBottom: '16px',
                    border: '1px solid #f0b90b'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <Mail size={20} color="#f0b90b" />
                      <span style={{ color: '#eaecef', fontWeight: '600' }}>Подтвердите Email</span>
                    </div>
                    <p style={{ color: '#848e9c', fontSize: '13px', marginBottom: '12px' }}>
                      Код отправлен на {emailMasked}
                    </p>
                    <input
                      type="text"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      style={{
                        width: '100%',
                        padding: '14px',
                        backgroundColor: '#1e2329',
                        border: '1px solid #2b3139',
                        borderRadius: '8px',
                        color: '#eaecef',
                        fontSize: '24px',
                        fontWeight: '600',
                        textAlign: 'center',
                        letterSpacing: '8px',
                        marginBottom: '12px',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => { setEmailStep(1); setEmailCode(''); }}
                        style={{
                          flex: 1,
                          padding: '12px',
                          backgroundColor: '#2b3139',
                          color: '#eaecef',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        Отмена
                      </button>
                      <button
                        onClick={verifyEmail2FA}
                        disabled={emailCode.length !== 6 || emailLoading}
                        style={{
                          flex: 1,
                          padding: '12px',
                          backgroundColor: emailCode.length === 6 ? '#f0b90b' : '#363c45',
                          color: emailCode.length === 6 ? '#0b0e11' : '#848e9c',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                          cursor: emailCode.length === 6 ? 'pointer' : 'not-allowed',
                        }}
                      >
                        {emailLoading ? 'Проверка...' : 'Подтвердить'}
                      </button>
                    </div>
                    <button
                      onClick={resendEmailCode}
                      disabled={emailLoading}
                      style={{
                        width: '100%',
                        marginTop: '8px',
                        padding: '8px',
                        backgroundColor: 'transparent',
                        color: '#f0b90b',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '13px',
                      }}
                    >
                      Отправить код повторно
                    </button>
                  </div>
                )}

                <div style={{ borderTop: '1px solid #2b3139', paddingTop: '16px', marginTop: '8px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', color: '#848e9c', fontSize: '13px', marginBottom: '8px' }}>
                      Введите код для отключения
                    </label>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      style={{
                        width: '100%',
                        padding: '14px',
                        backgroundColor: '#0b0e11',
                        border: '1px solid #2b3139',
                        borderRadius: '8px',
                        color: '#eaecef',
                        fontSize: '24px',
                        fontWeight: '600',
                        textAlign: 'center',
                        letterSpacing: '8px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <button
                    onClick={disable2FA}
                    disabled={loading || code.length !== 6}
                    style={{
                      width: '100%',
                      padding: '14px',
                      backgroundColor: '#f6465d',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading || code.length !== 6 ? 0.6 : 1,
                    }}
                  >
                    {loading ? 'Отключение...' : 'Отключить 2FA'}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Method Selection Before Setup */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', color: '#eaecef', fontSize: '14px', fontWeight: '500', marginBottom: '12px' }}>
                    Выберите метод защиты
                  </label>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <button
                      onClick={setup2FA}
                      disabled={loading}
                      style={{
                        padding: '14px 16px',
                        backgroundColor: '#0b0e11',
                        border: '1px solid #2b3139',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#f0b90b';
                        e.currentTarget.style.backgroundColor = '#f0b90b15';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#2b3139';
                        e.currentTarget.style.backgroundColor = '#0b0e11';
                      }}
                    >
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        backgroundColor: '#0ecb8120',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Smartphone size={20} color="#0ecb81" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>Google Authenticator</div>
                        <div style={{ color: '#848e9c', fontSize: '12px' }}>Рекомендуется • Самый надёжный</div>
                      </div>
                      <ChevronRight size={20} color="#848e9c" />
                    </button>

                    <button
                      onClick={setupEmail2FA}
                      disabled={emailLoading}
                      style={{
                        padding: '14px 16px',
                        backgroundColor: '#0b0e11',
                        border: '1px solid #2b3139',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#f0b90b';
                        e.currentTarget.style.backgroundColor = '#f0b90b15';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#2b3139';
                        e.currentTarget.style.backgroundColor = '#0b0e11';
                      }}
                    >
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        backgroundColor: '#f0b90b20',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Mail size={20} color="#f0b90b" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>Email код</div>
                        <div style={{ color: '#848e9c', fontSize: '12px' }}>Код на почту при входе</div>
                      </div>
                      <ChevronRight size={20} color="#848e9c" />
                    </button>
                  </div>
                </div>

                {/* Email 2FA Setup Step - When Not Yet Enabled */}
                {emailStep === 2 && (
                  <div style={{ 
                    backgroundColor: '#0b0e11', 
                    borderRadius: '12px', 
                    padding: '16px',
                    marginTop: '-12px',
                    border: '1px solid #f0b90b'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <Mail size={20} color="#f0b90b" />
                      <span style={{ color: '#eaecef', fontWeight: '600' }}>Подтвердите Email</span>
                    </div>
                    <p style={{ color: '#848e9c', fontSize: '13px', marginBottom: '12px' }}>
                      Код отправлен на {emailMasked}
                    </p>
                    <input
                      type="text"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      style={{
                        width: '100%',
                        padding: '14px',
                        backgroundColor: '#1e2329',
                        border: '1px solid #2b3139',
                        borderRadius: '8px',
                        color: '#eaecef',
                        fontSize: '24px',
                        fontWeight: '600',
                        textAlign: 'center',
                        letterSpacing: '8px',
                        marginBottom: '12px',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => { setEmailStep(1); setEmailCode(''); }}
                        style={{
                          flex: 1,
                          padding: '12px',
                          backgroundColor: '#2b3139',
                          color: '#eaecef',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        Отмена
                      </button>
                      <button
                        onClick={verifyEmail2FA}
                        disabled={emailCode.length !== 6 || emailLoading}
                        style={{
                          flex: 1,
                          padding: '12px',
                          backgroundColor: emailCode.length === 6 ? '#f0b90b' : '#363c45',
                          color: emailCode.length === 6 ? '#0b0e11' : '#848e9c',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                          cursor: emailCode.length === 6 ? 'pointer' : 'not-allowed',
                        }}
                      >
                        {emailLoading ? 'Проверка...' : 'Подтвердить'}
                      </button>
                    </div>
                  </div>
                )}

                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#f0b90b10',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                }}>
                  <AlertTriangle size={18} color="#f0b90b" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <p style={{ color: '#848e9c', fontSize: '12px', margin: 0, lineHeight: '1.5' }}>
                    После включения 2FA, при каждом входе в систему вам потребуется ввести код подтверждения
                  </p>
                </div>
              </>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <p style={{ color: '#848e9c', fontSize: '13px', marginBottom: '16px' }}>
                Отсканируйте QR-код в Google Authenticator
              </p>
              <div style={{
                width: '180px',
                height: '180px',
                backgroundColor: '#fff',
                borderRadius: '12px',
                margin: '0 auto 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {qrCode ? (
                  <img src={qrCode} alt="QR Code" style={{ width: '160px', height: '160px' }} />
                ) : (
                  <Smartphone size={60} color="#000" />
                )}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#848e9c', fontSize: '13px', marginBottom: '8px' }}>
                Или введите код вручную
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                backgroundColor: '#0b0e11',
                borderRadius: '8px',
              }}>
                <code style={{ flex: 1, color: '#eaecef', fontSize: '12px', wordBreak: 'break-all' }}>{secret}</code>
                <button onClick={handleCopy} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                  {copied ? <CheckCircle size={18} color="#0ecb81" /> : <Copy size={18} color="#848e9c" />}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#848e9c', fontSize: '13px', marginBottom: '8px' }}>
                Введите код из приложения
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#0b0e11',
                  border: '1px solid #2b3139',
                  borderRadius: '8px',
                  color: '#eaecef',
                  fontSize: '24px',
                  fontWeight: '600',
                  textAlign: 'center',
                  letterSpacing: '8px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setStep(1); setCode(''); }}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: '#2b3139',
                  color: '#eaecef',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Назад
              </button>
              <button
                onClick={verify2FA}
                disabled={loading || code.length !== 6}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: '#0ecb81',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading || code.length !== 6 ? 0.6 : 1,
                }}
              >
                {loading ? 'Проверка...' : 'Подтвердить'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== ACTIVE SESSIONS MODAL ====================
export const ActiveSessionsModal = ({ isOpen, onClose }) => {
  const { toast } = useNotification();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) loadSessions();
  }, [isOpen]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const res = await userAPI.getActiveSessions();
      setSessions(res.data);
    } catch (err) {
      // Mock data for demo
      setSessions([
        { id: '1', device: 'Chrome на Windows', ip: '192.168.1.1', location: 'Москва, Россия', last_active: new Date().toISOString(), is_current: true },
        { id: '2', device: 'Safari на iPhone', ip: '192.168.1.2', location: 'Москва, Россия', last_active: new Date(Date.now() - 3600000).toISOString(), is_current: false },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const terminateSession = async (sessionId) => {
    if (!window.confirm('Завершить эту сессию?')) return;
    try {
      await userAPI.terminateSession(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
      toast.success('Успешно', 'Сессия завершена');
    } catch (err) {
      toast.error('Ошибка', 'Ошибка завершения сессии');
    }
  };

  const terminateAllSessions = async () => {
    if (!window.confirm('Завершить все сессии кроме текущей?')) return;
    try {
      await userAPI.terminateAllSessions();
      setSessions(sessions.filter(s => s.is_current));
      toast.success('Успешно', 'Все сессии завершены');
    } catch (err) {
      toast.error('Ошибка', 'Ошибка завершения сессий');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1e2329',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: '20px', borderBottom: '1px solid #2b3139', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ color: '#eaecef', fontSize: '18px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Monitor size={20} color="#f0b90b" />
            Активные сессии
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={24} color="#848e9c" />
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#848e9c' }}>Загрузка...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sessions.map((session) => (
                <div key={session.id} style={{
                  backgroundColor: '#0b0e11',
                  borderRadius: '10px',
                  padding: '14px',
                  border: session.is_current ? '1px solid #0ecb81' : '1px solid #2b3139',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Monitor size={18} color={session.is_current ? '#0ecb81' : '#848e9c'} />
                      <span style={{ color: '#eaecef', fontSize: '14px', fontWeight: '500' }}>{session.device}</span>
                      {session.is_current && (
                        <span style={{ padding: '2px 6px', backgroundColor: '#0ecb8120', color: '#0ecb81', borderRadius: '4px', fontSize: '10px', fontWeight: '600' }}>
                          Текущая
                        </span>
                      )}
                    </div>
                    {!session.is_current && (
                      <button onClick={() => terminateSession(session.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                        <X size={18} color="#f6465d" />
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#848e9c', display: 'flex', gap: '16px' }}>
                    <span>{session.ip}</span>
                    <span>{session.location}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#5e6673', marginTop: '4px' }}>
                    Активность: {new Date(session.last_active).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {sessions.length > 1 && (
          <div style={{ padding: '16px', borderTop: '1px solid #2b3139' }}>
            <button
              onClick={terminateAllSessions}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#f6465d20',
                color: '#f6465d',
                border: '1px solid #f6465d',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Завершить все другие сессии
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== IP WHITELIST MODAL ====================
export const IPWhitelistModal = ({ isOpen, onClose }) => {
  const { toast } = useNotification();
  const [ips, setIps] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (isOpen) loadWhitelist();
  }, [isOpen]);

  const loadWhitelist = async () => {
    setLoading(true);
    try {
      const res = await userAPI.getIPWhitelist();
      setIps(res.data.ips || []);
      setEnabled(res.data.enabled || false);
    } catch (err) {
      setIps(['192.168.1.1', '10.0.0.1']);
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const addIP = async () => {
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(newIp)) {
      toast.warning('Внимание', 'Неверный формат IP');
      return;
    }
    try {
      await userAPI.addIPToWhitelist({ ip: newIp });
      setIps([...ips, newIp]);
      setNewIp('');
      toast.success('Добавлено', `IP ${newIp} добавлен в белый список`);
    } catch (err) {
      toast.error('Ошибка', 'Ошибка добавления IP');
    }
  };

  const removeIP = async (ip) => {
    try {
      await userAPI.removeIPFromWhitelist({ ip });
      setIps(ips.filter(i => i !== ip));
      toast.success('Удалено', `IP ${ip} удалён из белого списка`);
    } catch (err) {
      toast.error('Ошибка', 'Ошибка удаления IP');
    }
  };

  const toggleEnabled = async () => {
    try {
      await userAPI.toggleIPWhitelist({ enabled: !enabled });
      setEnabled(!enabled);
      toast.info('Настройки обновлены', enabled ? 'IP Whitelist отключён' : 'IP Whitelist включён');
    } catch (err) {
      toast.error('Ошибка', 'Ошибка обновления настроек');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1e2329',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '450px',
          padding: '24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: '#eaecef', fontSize: '18px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Globe size={20} color="#f0b90b" />
            Белый список IP
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={24} color="#848e9c" />
          </button>
        </div>

        {/* Toggle */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px',
          backgroundColor: '#0b0e11',
          borderRadius: '10px',
          marginBottom: '20px',
        }}>
          <div>
            <div style={{ color: '#eaecef', fontSize: '14px', fontWeight: '500' }}>Включить белый список</div>
            <div style={{ color: '#848e9c', fontSize: '12px' }}>Вывод только с указанных IP</div>
          </div>
          <button
            onClick={toggleEnabled}
            style={{
              width: '48px',
              height: '26px',
              borderRadius: '13px',
              backgroundColor: enabled ? '#0ecb81' : '#2b3139',
              border: 'none',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background-color 0.2s',
            }}
          >
            <div style={{
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              position: 'absolute',
              top: '2px',
              left: enabled ? '24px' : '2px',
              transition: 'left 0.2s',
            }} />
          </button>
        </div>

        {/* Add IP */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <input
            type="text"
            value={newIp}
            onChange={(e) => setNewIp(e.target.value)}
            placeholder="192.168.1.1"
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#0b0e11',
              border: '1px solid #2b3139',
              borderRadius: '8px',
              color: '#eaecef',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <button
            onClick={addIP}
            style={{
              padding: '12px 16px',
              backgroundColor: '#f0b90b',
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Plus size={16} />
          </button>
        </div>

        {/* IP List */}
        <div style={{ maxHeight: '200px', overflow: 'auto' }}>
          {ips.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#848e9c', fontSize: '13px' }}>
              Нет IP адресов в списке
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {ips.map((ip, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  backgroundColor: '#0b0e11',
                  borderRadius: '8px',
                }}>
                  <span style={{ color: '#eaecef', fontSize: '14px', fontFamily: 'monospace' }}>{ip}</span>
                  <button onClick={() => removeIP(ip)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                    <Trash2 size={16} color="#f6465d" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default { ChangePasswordModal, TwoFactorModal, ActiveSessionsModal, IPWhitelistModal };
